import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { DEFAULT_SEO, SITE_METADATA } from "./siteMetadata";
import { getCmsLocaleFromLanguage } from "../api/cmsLocale";

const LANGUAGE_QUERY_PARAM = "lang";

function splitPathAndQuery(value = "/") {
  const rawValue = String(value || "/").trim() || "/";
  const [withoutHash] = rawValue.split("#");
  const [rawPath, rawQuery = ""] = withoutHash.split("?");

  return {
    pathname: normalizePath(rawPath || "/"),
    params: new URLSearchParams(rawQuery),
  };
}

function normalizePath(path = "/") {
  if (!path || path === "/") return "/";

  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
}

function buildPathWithQuery(pathname = "/", params = new URLSearchParams()) {
  const query = params.toString();
  return `${normalizePath(pathname)}${query ? `?${query}` : ""}`;
}

function buildLocalizedPath(pathname = "/", locale = "me") {
  const { pathname: cleanPath, params } = splitPathAndQuery(pathname);

  if (locale === "en") {
    params.set(LANGUAGE_QUERY_PARAM, "en");
  } else {
    params.delete(LANGUAGE_QUERY_PARAM);
  }

  return buildPathWithQuery(cleanPath, params);
}

function toAbsoluteUrl(path = "/") {
  if (!path) return SITE_METADATA.siteUrl;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_METADATA.siteUrl}${normalized}`;
}

function getLanguageTag(language) {
  return getCmsLocaleFromLanguage(language) === "en" ? "en" : "sr-Latn-ME";
}

function getOgLocale(language) {
  return getCmsLocaleFromLanguage(language) === "en" ? "en_US" : "sr_ME";
}

function normalizeText(value = "", fallback = "") {
  return String(value || fallback || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildSeoTitle(value, fallback = DEFAULT_SEO.title) {
  const normalized = normalizeText(value, fallback);
  return normalized || fallback;
}

function buildSeoDescription(value, fallback = DEFAULT_SEO.description) {
  const normalized = normalizeText(value, fallback);

  if (normalized.length <= 170) return normalized;
  return `${normalized.slice(0, 167).trim()}…`;
}

function positiveInteger(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function serializeJsonLd(schema) {
  return JSON.stringify(schema).replace(/</g, "\\u003c");
}

function buildBreadcrumbJsonLd(breadcrumbs = []) {
  if (!breadcrumbs.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: toAbsoluteUrl(crumb.path),
    })),
  };
}

function buildDefaultPublisher() {
  return {
    "@type": "CollegeOrUniversity",
    name: SITE_METADATA.brandName,
    url: SITE_METADATA.siteUrl,
    logo: {
      "@type": "ImageObject",
      url: toAbsoluteUrl(SITE_METADATA.logoImage),
    },
    email: SITE_METADATA.contactEmail,
    telephone: SITE_METADATA.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE_METADATA.streetAddress,
      addressLocality: SITE_METADATA.addressLocality,
      postalCode: SITE_METADATA.postalCode,
      addressCountry: SITE_METADATA.addressCountry,
    },
  };
}

function buildPageJsonLd(meta, canonical) {
  const pageType = meta.pageType || "WebPage";

  if (pageType === "Course") {
    return {
      "@context": "https://schema.org",
      "@type": "Course",
      name: meta.courseTitle || meta.title,
      description: meta.description,
      provider: buildDefaultPublisher(),
    };
  }

  if (pageType === "FAQPage") {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      name: meta.title,
      description: meta.description,
      url: canonical,
      publisher: buildDefaultPublisher(),
    };
  }

  if (pageType === "ContactPage") {
    return {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: meta.title,
      description: meta.description,
      url: canonical,
      mainEntity: buildDefaultPublisher(),
    };
  }

  if (pageType === "WebSite") {
    return {
      "@context": "https://schema.org",
      "@type": "CollegeOrUniversity",
      name: SITE_METADATA.brandName,
      alternateName: SITE_METADATA.siteName,
      url: SITE_METADATA.siteUrl,
      logo: toAbsoluteUrl(SITE_METADATA.logoImage),
      email: SITE_METADATA.contactEmail,
      telephone: SITE_METADATA.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE_METADATA.streetAddress,
        addressLocality: SITE_METADATA.addressLocality,
        postalCode: SITE_METADATA.postalCode,
        addressCountry: SITE_METADATA.addressCountry,
      },
    };
  }

  return {
    "@context": "https://schema.org",
    "@type": pageType,
    name: meta.title,
    description: meta.description,
    url: canonical,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_METADATA.siteName,
      url: SITE_METADATA.siteUrl,
    },
    publisher: buildDefaultPublisher(),
  };
}

export function SEO({
  title,
  description,
  pathname = "/",
  image,
  imageAlt,
  imageType,
  imageWidth,
  imageHeight,
  pageType,
  section,
  breadcrumbs = [],
  noindex = false,
  jsonLd = [],
  article = null,
  courseTitle,
  canonicalLocale = null,
  englishAlternateAvailable = true,
  programName,
  studyLevel,
  disableLanguageAlternates = false,
  contentLanguage,
  openGraphLocale,
  alternateOpenGraphLocale,
}) {
  const { i18n } = useTranslation();
  const activeLocale = getCmsLocaleFromLanguage(i18n.language);
  const resolvedCanonicalLocale = canonicalLocale || activeLocale;
  const canonicalPath = buildLocalizedPath(pathname, resolvedCanonicalLocale);
  const canonical = toAbsoluteUrl(canonicalPath);
  const finalTitle = buildSeoTitle(title, DEFAULT_SEO.title);
  const finalDescription = buildSeoDescription(description, DEFAULT_SEO.description);
  const finalPageType = pageType || DEFAULT_SEO.pageType;
  const ogImage = toAbsoluteUrl(image || SITE_METADATA.defaultOgImage);
  const resolvedImageAlt = normalizeText(
    imageAlt,
    image ? finalTitle : SITE_METADATA.defaultOgImageAlt || finalTitle,
  );
  const resolvedImageType = imageType || (!image ? SITE_METADATA.defaultOgImageType : undefined);
  const resolvedImageWidth = positiveInteger(
    imageWidth || (!image ? SITE_METADATA.defaultOgImageWidth : null),
  );
  const resolvedImageHeight = positiveInteger(
    imageHeight || (!image ? SITE_METADATA.defaultOgImageHeight : null),
  );
  const htmlLang = contentLanguage || getLanguageTag(resolvedCanonicalLocale);
  const ogLocale = openGraphLocale || getOgLocale(resolvedCanonicalLocale);
  const alternateOgLocale = alternateOpenGraphLocale
    || (resolvedCanonicalLocale === "en" ? "sr_ME" : "en_US");
  const alternateMeUrl = toAbsoluteUrl(buildLocalizedPath(pathname, "me"));
  const alternateEnUrl = toAbsoluteUrl(buildLocalizedPath(pathname, "en"));
  const shouldRenderEnglishAlternate = englishAlternateAvailable !== false;
  const customSchemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [];

  const meta = {
    title: finalTitle,
    description: finalDescription,
    pageType: finalPageType,
    courseTitle,
    programName,
    studyLevel,
  };

  const hasEquivalentCustomSchema = customSchemas.some(
    (schema) => schema?.["@type"] === finalPageType,
  );
  const schemas = [
    article || hasEquivalentCustomSchema ? null : buildPageJsonLd(meta, canonical),
    buildBreadcrumbJsonLd(breadcrumbs),
    ...customSchemas,
  ].filter(Boolean);

  if (article) {
    schemas.unshift({
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.headline || finalTitle,
      description: finalDescription,
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      image: article.image ? [toAbsoluteUrl(article.image)] : [ogImage],
      mainEntityOfPage: canonical,
      inLanguage: htmlLang,
      articleSection: article.section || section,
      author: {
        "@type": "Organization",
        name: SITE_METADATA.brandName,
      },
      publisher: buildDefaultPublisher(),
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <html lang={htmlLang} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      <meta name="author" content={SITE_METADATA.brandName} />
      <meta name="publisher" content={SITE_METADATA.brandName} />
      <meta property="og:site_name" content={SITE_METADATA.siteName} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta
        property="og:type"
        content={article || finalPageType === "Article" ? "article" : "website"}
      />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      {resolvedImageType && <meta property="og:image:type" content={resolvedImageType} />}
      {resolvedImageWidth && <meta property="og:image:width" content={resolvedImageWidth} />}
      {resolvedImageHeight && <meta property="og:image:height" content={resolvedImageHeight} />}
      <meta property="og:locale" content={ogLocale} />
      {shouldRenderEnglishAlternate && (
        <meta property="og:locale:alternate" content={alternateOgLocale} />
      )}
      {article?.datePublished && (
        <meta property="article:published_time" content={article.datePublished} />
      )}
      {(article?.dateModified || article?.datePublished) && (
        <meta
          property="article:modified_time"
          content={article.dateModified || article.datePublished}
        />
      )}
      {(article || finalPageType === "Article") && (article?.section || section) && (
        <meta property="article:section" content={article?.section || section} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />
      <link rel="canonical" href={canonical} />
      {!disableLanguageAlternates && (
        <>
          <link rel="alternate" hrefLang="sr-Latn-ME" href={alternateMeUrl} />
          {shouldRenderEnglishAlternate && (
            <link rel="alternate" hrefLang="en" href={alternateEnUrl} />
          )}
          <link rel="alternate" hrefLang="x-default" href={alternateMeUrl} />
        </>
      )}
      {schemas.map((schema, index) => (
        <script key={`${schema["@type"] || "schema"}-${index}`} type="application/ld+json">
          {serializeJsonLd(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
